import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import {useBoolean} from '../../../../../utils/hooks';
import SMCLink from '../../../../../components/Simple/SMCLink';
import ButtonContainer from '../../../../../components/Simple/ButtonContainer';
import SMCButton from '../../../../../components/Simple/SMCButton';
import SMCTable from '../../../../../components/core/SMCTable/SMCTable';
import InternalNetworkEditorModal from '../../../../PopupPages/InternalNetworkEditorModal';
import {removeItemsFromArrayByIndexes} from '../../../../../utils/utils';

const topologySchema = GatewayGetSchema.pages.vpnInternalNetworkTopology.topologySchema;

function netmask2CIDR(netmask) {
    return netmask
        .split('.')
        .map(Number)
        .map(part => (part >>> 0).toString(2))
        .join('')
        .split('1').length - 1;
}

function GatewayVpnTopologyTable({
    topologyList,
    setTopologyList
}) {
    const { t } = useTranslation();

    const [isEditTopologyModalOpen, openEditTopologyModal, closeEditTopologyModal] = useBoolean(false);
    const [selectedObjects, setSelectedObjects] = useState([]);
    const [editObject, setEditObject] = useState(null);
    const [editObjectIndex, setEditObjectIndex] = useState(null);

    const deleteSelectedTopologies = useCallback(() => {
        if (topologyList && topologyList.length && selectedObjects.length) {
            const newTopologyList = removeItemsFromArrayByIndexes(topologyList, selectedObjects);
            setSelectedObjects([]);
            setTopologyList(newTopologyList);
        }
    }, [topologyList, selectedObjects, setTopologyList]);

    const editTopologyOnCancel = useCallback(() => {
        // Set edit object and edit object key to null, to signify 
        setEditObject(null);
        setEditObjectIndex(null);
        closeEditTopologyModal();
    }, [closeEditTopologyModal]);

    const addTopologyCallback = useCallback((newTopologyObject) => {
        const newTopologyList = topologyList ? [...topologyList] : [];
        newTopologyList.push(newTopologyObject);
        setTopologyList(newTopologyList);
        closeEditTopologyModal();
    }, [topologyList, closeEditTopologyModal, setTopologyList]);

    const editTopologyCallback = useCallback((newTopologyObject) => {
        const newTopologyList = topologyList ? [...topologyList] : [];
        newTopologyList[editObjectIndex] = newTopologyObject;
        setTopologyList(newTopologyList);
        setEditObject(null);
        setEditObjectIndex(null);
        closeEditTopologyModal();
    }, [topologyList, closeEditTopologyModal, editObjectIndex, setTopologyList]);

    const EditCell = React.memo(({value, object, index}) => {
        return (
            <span>
                <SMCLink
                    inline
                    onClick={() => {
                        setEditObject(object);
                        setEditObjectIndex(index);
                        openEditTopologyModal();
                    }}
                >
                    { value }
                </SMCLink>
            </span>
        );
    });

    const MaskCell = React.memo(({value}) => {
        return (
            <span>
                {value} [/{value && netmask2CIDR(value)}]
            </span>
        );
    });

    return (
        <>
            <ButtonContainer>
                <SMCButton variant="ghost" icon="new" onClick={openEditTopologyModal}>{t('SMC_GEN_NEW')}</SMCButton>
                <SMCButton variant="ghost" disabled={!selectedObjects.length} icon="delete" onClick={deleteSelectedTopologies}>{t('SMC_GEN_DELETE')}</SMCButton>
            </ButtonContainer>
            <SMCTable
                columns={[
                    {id: 'name',    accessor: topologySchema.name,    header: t('SMP.NAME'),                                            width: 150, Cell: EditCell },
                    {id: 'network', accessor: topologySchema.network, header: t('SMP.GATEWAYS.PAGE.VPN.INTERNAL_NET_TOPOLOGY.NETWORK'), width: 100, Cell: EditCell },
                    {id: 'mask',    accessor: topologySchema.mask,    header: t('SMP.GATEWAYS.PAGE.VPN.INTERNAL_NET_TOPOLOGY.MASK'),    width: 150, Cell: MaskCell }
                ]}
                rowIdSelector={(obj, index) => index}
                selectedObjects={selectedObjects}
                setSelectedObjects={setSelectedObjects}
                content={topologyList}
                hasCheckAll={true}
                width={500}
                maxHeight={500}
            />
            <InternalNetworkEditorModal
                isOpen={isEditTopologyModalOpen}
                onSuccess={editObject ? editTopologyCallback : addTopologyCallback}
                onRequestClose={editTopologyOnCancel}
                defaultObject={editObject}
            />
        </>
    );

};

export default GatewayVpnTopologyTable;